import React, { useState } from "react";
import styled from '@emotion/styled';
import Button from "../../../../components/general/Button";
import ModalHeader from "../../../../components/general/ModalHeader";
import FooterBar from "../../../../components/structure/FooterBar";
import ModalLayout from "../../../../components/structure/ModalLayout";
import { style_border_default } from "../../../../constants/stylesValues";
import { color_gradients_green_shift, color_shades_darkest, color_text_link, color_defaults_white, color_shades_lighter, color_shades_light, color_colors_decline } from "../../../../constants/colors";
import { css } from "@emotion/css";
import { FormikProps } from "formik";
import useWindowSize from "../../../../hooks/UseWindowSize";
import CustomScrollbars from "../../../../components/structure/CustomScrollbars";
import { TelephonyDeploymentProps } from "./TelephonyDeploymentForm";
import { TelephonyDeploymentFormData } from "./TelephonyDeploymentFormData";
import { TelephonyDeploymentResponse } from "../../../../models/telephonyDeployment/TelephonyDeploymentResponse";
import TextField from "../../../../components/forms/TextField";
import TextAreaField from "../../../../components/forms/TextAreaField";
import IconTooltip from "../../../../components/general/IconTooltip";
import StyledDropdown from "../customAssistantConfigurations/StyledDropdown";
import RadioGroup from "../../../../components/forms/RadioGroup";
import { autoTransferPreviouslyTransferredOptions, attemptInitialTransferOptions, callRecordingOptions, disableBargeInOptions, disableSpamFilterOptions, disableSpeechStartedOptions, disableWelcomeMessageBargeInOptions, googleVoiceOptions, initialTransferWithCallingNumberOptions, mergeTurnsAfterPausesOptions, noTrackingOptions, useProactiveOptions, virtualNumberOptions, tryWebsocketReconnectionOnWelcomeOptions, useGenerativeProactiveOptions, SpeechCleaningOptions, useForOutboundCallingOptions } from "./TelephonyDeploymentFormFieldOptions";
import ContentCollapsingSection from "../../../../components/structure/ContentCollapsingSection";
import { formatPhoneNumber } from "../../../../../clients/spark/scenes/basicOrganizationApplications/components/ApplicationItem";
import { availableASRProviders } from "../../../../constants/telephonyConstants.tsx/defaultTelephonyDeploymentSettings";
import { defaultVoice, defaultRimeVoice } from "../../../../constants/sparkConstants/voices";
import { defaultTelephonyDeploymentSettings } from '../../../../constants/telephonyConstants.tsx/defaultTelephonyDeploymentSettings';
import { voiceOptions } from "../../../../constants/sparkConstants/voiceOptions";
import { TTSOption } from "../../../../components/forms/RadioCardGroupWithAudio";
import { PHONE_PLATFORMS } from "../../../../constants/phonePlatforms";
import NotificationEmailsManager from "./NotificationEmailsManager";
import TelephonyDeploymentContainer from "../../../../hooks/TelephonyDeploymentContainer";

const advancedSettingsIcon = require('../../../../content/images/application-settings/advanced-settings.svg');

const MODAL_SCROLLBAR_SUBTRACTION: number = 200;

const processAudioOptions = [
    { label: 'Yes', value: true },
    { label: 'No', value: false }
];

const countryOptions = [
    { label: "United States", value: "US" },
    { label: "Canada", value: "CA" }
];

const TelephonyDeployment: React.FC<TelephonyDeploymentProps & FormikProps<TelephonyDeploymentFormData>> = (props) => {
    const windowSize = useWindowSize();
    const telephonyDeploymentContainer = TelephonyDeploymentContainer.useContainer();

    // Initialize locale configurations with existing deployments if available
    const initializeLocaleConfigurations = () => {
        if (props.selectedPhoneNumberDeployments && props.selectedPhoneNumberDeployments.length > 1) {
            // Multiple existing deployments - initialize with all of them
            const configs: { [locale: string]: Partial<TelephonyDeploymentFormData> & { ttsOptions?: any[] } } = {};
            props.selectedPhoneNumberDeployments.forEach(deployment => {
                configs[deployment.locale] = {
                    noTracking: deployment.noTracking ?? defaultTelephonyDeploymentSettings.noTracking,
                    phoneNumberStartsWith: deployment.phoneNumber || defaultTelephonyDeploymentSettings.phoneNumberStartsWith,
                    locale: deployment.locale,
                    asrEndpointing: deployment.asrEndpointing ?? defaultTelephonyDeploymentSettings.asrEndpointing,
                    asrProvider: deployment.asrProvider ?? defaultTelephonyDeploymentSettings.asrProvider,
                    asrModel: deployment.asrModel ?? defaultTelephonyDeploymentSettings.asrModel,
                    asrTier: deployment.asrTier ?? defaultTelephonyDeploymentSettings.asrTier,
                    performLanguageDetection: deployment.performLanguageDetection ?? defaultTelephonyDeploymentSettings.performLanguageDetection,
                    dtmfTurnsToListen: deployment.dtmfTurnsToListen ?? defaultTelephonyDeploymentSettings.dtmfTurnsToListen,
                    ttsPronunciationsMap: deployment.ttsPronunciationsMap ?? defaultTelephonyDeploymentSettings.ttsPronunciationsMap,
                    firstMessageDelayMS: deployment.firstMessageDelayMS ?? defaultTelephonyDeploymentSettings.firstMessageDelayMS,
                    mergeTurnsAfterPauses: deployment.mergeTurnsAfterPauses ?? defaultTelephonyDeploymentSettings.mergeTurnsAfterPauses,
                    mergeTurnTimeoutMS: deployment.mergeTurnTimeoutMS ?? defaultTelephonyDeploymentSettings.mergeTurnTimeoutMS,
                    disableBargeIn: deployment.disableBargeIn ?? defaultTelephonyDeploymentSettings.disableBargeIn,
                    repromptAttempts: deployment.repromptAttempts ?? defaultTelephonyDeploymentSettings.repromptAttempts,
                    repromptDelaySeconds: deployment.repromptDelaySeconds ?? defaultTelephonyDeploymentSettings.repromptDelaySeconds,
                    name: deployment.name ?? defaultTelephonyDeploymentSettings.name,
                    ttsOptions: deployment.ttsOptions?.length > 0 ? deployment.ttsOptions :
                        deployment.ttsProvider && deployment.ttsVoice ?
                            [{ ttsProvider: deployment.ttsProvider, ttsVoice: deployment.ttsVoice }] :
                            defaultTelephonyDeploymentSettings.ttsOptions,
                    proactiveResponseDelayMS: deployment.proactiveResponseDelayMS ?? defaultTelephonyDeploymentSettings.proactiveResponseDelayMS,
                    proactiveResponses: deployment.proactiveResponses ?? defaultTelephonyDeploymentSettings.proactiveResponses,
                    useProactiveResponses: deployment.useProactiveResponses ?? defaultTelephonyDeploymentSettings.useProactiveResponses,
                    maxProactiveResponseAttempts: deployment.maxProactiveResponseAttempts ?? defaultTelephonyDeploymentSettings.maxProactiveResponseAttempts,
                    followUpProactiveResponses: deployment.followUpProactiveResponses ?? defaultTelephonyDeploymentSettings.followUpProactiveResponses,
                    disableWelcomeMessageBargeIn: deployment.disableWelcomeMessageBargeIn ?? defaultTelephonyDeploymentSettings.disableWelcomeMessageBargeIn,
                    disableSpeechStarted: deployment.disableSpeechStarted ?? defaultTelephonyDeploymentSettings.disableSpeechStarted,
                    speechStartedUnconfirmedBargeInLimit: deployment.speechStartedUnconfirmedBargeInLimit ?? defaultTelephonyDeploymentSettings.speechStartedUnconfirmedBargeInLimit,
                    speechStartedPauseMS: deployment.speechStartedPauseMS ?? defaultTelephonyDeploymentSettings.speechStartedPauseMS,
                    speechStartedRequiredBufferCount: deployment.speechStartedRequiredBufferCount ?? defaultTelephonyDeploymentSettings.speechStartedRequiredBufferCount,
                    callRecordingEnabled: deployment.callRecordingEnabled ?? defaultTelephonyDeploymentSettings.callRecordingEnabled,
                    speechStartedLevel: deployment.speechStartedLevel ?? defaultTelephonyDeploymentSettings.speechStartedLevel,
                    attemptInitialTransfer: deployment.attemptInitialTransfer ?? defaultTelephonyDeploymentSettings.attemptInitialTransfer,
                    initialTransferTimeoutSeconds: deployment.initialTransferTimeoutSeconds ?? defaultTelephonyDeploymentSettings.initialTransferTimeoutSeconds,
                    initialTransferTarget: deployment.initialTransferTarget ?? defaultTelephonyDeploymentSettings.initialTransferTarget,
                    initialTransferWithCallingNumber: deployment.initialTransferWithCallingNumber ?? defaultTelephonyDeploymentSettings.initialTransferWithCallingNumber,
                    postProactiveResponsePauseDelayMS: deployment.postProactiveResponsePauseDelayMS ?? defaultTelephonyDeploymentSettings.postProactiveResponsePauseDelayMS,
                    virtualNumber: deployment.virtualNumber ?? defaultTelephonyDeploymentSettings.virtualNumber,
                    useVirtualNumber: deployment.virtualNumber?.length > 0,
                    shouldProcessAudio: deployment.shouldProcessAudio ?? defaultTelephonyDeploymentSettings.shouldProcessAudio,
                    disableSpamDetection: deployment.disableSpamDetection ?? defaultTelephonyDeploymentSettings.disableSpamDetection,
                    immediateTransferHourCutoff: deployment.immediateTransferHourCutoff ?? defaultTelephonyDeploymentSettings.immediateTransferHourCutoff,
                    aiReengageSecondsAfterImmediateTransfer: deployment.aiReengageSecondsAfterImmediateTransfer ?? defaultTelephonyDeploymentSettings.aiReengageSecondsAfterImmediateTransfer,
                    spamDetectionPromptOverride: deployment.spamDetectionPromptOverride ?? defaultTelephonyDeploymentSettings.spamDetectionPromptOverride,
                    spamDetectionTolerance: deployment.spamDetectionTolerance ?? defaultTelephonyDeploymentSettings.spamDetectionTolerance,
                    spamDetectionTemperature: deployment.spamDetectionTemperature ?? defaultTelephonyDeploymentSettings.spamDetectionTemperature,
                    spamDetectionGenAiProvider: deployment.spamDetectionGenAiProvider ?? defaultTelephonyDeploymentSettings.spamDetectionGenAiProvider,
                    tryWebsocketReconnectionOnWelcome: deployment.tryWebsocketReconnectionOnWelcome ?? defaultTelephonyDeploymentSettings.tryWebsocketReconnectionOnWelcome,
                    platform: deployment.platform ?? PHONE_PLATFORMS.VONAGE,
                    useGenerativeProactiveResponse: deployment.useGenerativeProactiveResponse ?? defaultTelephonyDeploymentSettings.useGenerativeProactiveResponse,
                    generativeProactiveResponsePrompt: deployment.generativeProactiveResponsePrompt ?? defaultTelephonyDeploymentSettings.generativeProactiveResponsePrompt,
                    lastProactiveResponsesToDisallow: deployment.lastProactiveResponsesToDisallow ?? defaultTelephonyDeploymentSettings.lastProactiveResponsesToDisallow,
                    notificationEmails: deployment.notificationEmails ?? defaultTelephonyDeploymentSettings.notificationEmails,
                    outboundCallerIdNumber: deployment.outboundCallerIdNumber ?? defaultTelephonyDeploymentSettings.outboundCallerIdNumber,
                    useForOutboundCalling: deployment.useForOutboundCalling ?? defaultTelephonyDeploymentSettings.useForOutboundCalling,
                    disableAssistantSpeechCleaning: deployment.disableAssistantSpeechCleaning ?? defaultTelephonyDeploymentSettings.disableAssistantSpeechCleaning,
                    optInText: deployment.optInText ?? defaultTelephonyDeploymentSettings.optInText,
                };
            });
            return configs;
        } else {
            // Single deployment or new deployment
            return { [props.values.locale]: { ...props.values } };
        }
    };

    // State for managing multiple locale configurations
    const [localeConfigurations, setLocaleConfigurations] = useState(initializeLocaleConfigurations);
    const [activeLocale, setActiveLocale] = useState<string>(props.values.locale);
    const [modifiedLocales, setModifiedLocales] = useState<Set<string>>(new Set());
    const [switchingToLocale, setSwitchingToLocale] = useState<string | null>(null);

    // Function to create new locale configuration
    const handleCreateNewLocaleConfiguration = (newLocale: string) => {
        const currentConfig = localeConfigurations[activeLocale];
        const newConfig = {
            ...currentConfig,
            locale: newLocale,
            name: '', // Clear the name field as requested
            country: currentConfig.country || props.values.country // Copy the phone number country code
        };

        setLocaleConfigurations(prev => ({
            ...prev,
            [newLocale]: newConfig
        }));
        setActiveLocale(newLocale);
        setModifiedLocales(prev => new Set(prev).add(newLocale));

        // Update form values to the new configuration
        Object.keys(newConfig).forEach(key => {
            props.setFieldValue(key, (newConfig as any)[key]);
        });
    };

    // Function to switch between locale tabs
    const handleLocaleTabChange = async (locale: string) => {
        if (locale === activeLocale) return;

        setSwitchingToLocale(locale);

        // Save current form values to current locale
        setLocaleConfigurations(prev => ({
            ...prev,
            [activeLocale]: { ...props.values }
        }));
        await new Promise(resolve => setTimeout(resolve, 1));

        // Switch to new locale
        setActiveLocale(locale);
        let targetConfig = localeConfigurations[locale];

        // If we have multiple existing deployments, also update the selected deployment
        if (props.selectedPhoneNumberDeployments && props.selectedPhoneNumberDeployments.length > 1) {
            const correspondingDeployment = props.selectedPhoneNumberDeployments.find(d => d.locale === locale);
            if (correspondingDeployment) {
                if (props.onSelectDeployment) {
                    props.onSelectDeployment(correspondingDeployment);
                }

                // If we don't have a config for this locale yet, create one from the deployment
                if (!targetConfig) {
                    targetConfig = {
                        noTracking: correspondingDeployment.noTracking ?? defaultTelephonyDeploymentSettings.noTracking,
                        phoneNumberStartsWith: correspondingDeployment.phoneNumber || defaultTelephonyDeploymentSettings.phoneNumberStartsWith,
                        locale: correspondingDeployment.locale,
                        asrEndpointing: correspondingDeployment.asrEndpointing ?? defaultTelephonyDeploymentSettings.asrEndpointing,
                        asrProvider: correspondingDeployment.asrProvider ?? defaultTelephonyDeploymentSettings.asrProvider,
                        asrModel: correspondingDeployment.asrModel ?? defaultTelephonyDeploymentSettings.asrModel,
                        asrTier: correspondingDeployment.asrTier ?? defaultTelephonyDeploymentSettings.asrTier,
                        performLanguageDetection: correspondingDeployment.performLanguageDetection ?? defaultTelephonyDeploymentSettings.performLanguageDetection,
                        dtmfTurnsToListen: correspondingDeployment.dtmfTurnsToListen ?? defaultTelephonyDeploymentSettings.dtmfTurnsToListen,
                        ttsPronunciationsMap: correspondingDeployment.ttsPronunciationsMap ?? defaultTelephonyDeploymentSettings.ttsPronunciationsMap,
                        firstMessageDelayMS: correspondingDeployment.firstMessageDelayMS ?? defaultTelephonyDeploymentSettings.firstMessageDelayMS,
                        mergeTurnsAfterPauses: correspondingDeployment.mergeTurnsAfterPauses ?? defaultTelephonyDeploymentSettings.mergeTurnsAfterPauses,
                        mergeTurnTimeoutMS: correspondingDeployment.mergeTurnTimeoutMS ?? defaultTelephonyDeploymentSettings.mergeTurnTimeoutMS,
                        disableBargeIn: correspondingDeployment.disableBargeIn ?? defaultTelephonyDeploymentSettings.disableBargeIn,
                        repromptAttempts: correspondingDeployment.repromptAttempts ?? defaultTelephonyDeploymentSettings.repromptAttempts,
                        repromptDelaySeconds: correspondingDeployment.repromptDelaySeconds ?? defaultTelephonyDeploymentSettings.repromptDelaySeconds,
                        name: correspondingDeployment.name ?? defaultTelephonyDeploymentSettings.name,
                        ttsOptions: correspondingDeployment.ttsOptions?.length > 0 ? correspondingDeployment.ttsOptions :
                            correspondingDeployment.ttsProvider && correspondingDeployment.ttsVoice ?
                                [{ ttsProvider: correspondingDeployment.ttsProvider, ttsVoice: correspondingDeployment.ttsVoice }] :
                                defaultTelephonyDeploymentSettings.ttsOptions,
                        proactiveResponseDelayMS: correspondingDeployment.proactiveResponseDelayMS ?? defaultTelephonyDeploymentSettings.proactiveResponseDelayMS,
                        proactiveResponses: correspondingDeployment.proactiveResponses ?? defaultTelephonyDeploymentSettings.proactiveResponses,
                        useProactiveResponses: correspondingDeployment.useProactiveResponses ?? defaultTelephonyDeploymentSettings.useProactiveResponses,
                        maxProactiveResponseAttempts: correspondingDeployment.maxProactiveResponseAttempts ?? defaultTelephonyDeploymentSettings.maxProactiveResponseAttempts,
                        followUpProactiveResponses: correspondingDeployment.followUpProactiveResponses ?? defaultTelephonyDeploymentSettings.followUpProactiveResponses,
                        disableWelcomeMessageBargeIn: correspondingDeployment.disableWelcomeMessageBargeIn ?? defaultTelephonyDeploymentSettings.disableWelcomeMessageBargeIn,
                        disableSpeechStarted: correspondingDeployment.disableSpeechStarted ?? defaultTelephonyDeploymentSettings.disableSpeechStarted,
                        speechStartedUnconfirmedBargeInLimit: correspondingDeployment.speechStartedUnconfirmedBargeInLimit ?? defaultTelephonyDeploymentSettings.speechStartedUnconfirmedBargeInLimit,
                        speechStartedPauseMS: correspondingDeployment.speechStartedPauseMS ?? defaultTelephonyDeploymentSettings.speechStartedPauseMS,
                        speechStartedRequiredBufferCount: correspondingDeployment.speechStartedRequiredBufferCount ?? defaultTelephonyDeploymentSettings.speechStartedRequiredBufferCount,
                        callRecordingEnabled: correspondingDeployment.callRecordingEnabled ?? defaultTelephonyDeploymentSettings.callRecordingEnabled,
                        speechStartedLevel: correspondingDeployment.speechStartedLevel ?? defaultTelephonyDeploymentSettings.speechStartedLevel,
                        attemptInitialTransfer: correspondingDeployment.attemptInitialTransfer ?? defaultTelephonyDeploymentSettings.attemptInitialTransfer,
                        initialTransferTimeoutSeconds: correspondingDeployment.initialTransferTimeoutSeconds ?? defaultTelephonyDeploymentSettings.initialTransferTimeoutSeconds,
                        initialTransferTarget: correspondingDeployment.initialTransferTarget ?? defaultTelephonyDeploymentSettings.initialTransferTarget,
                        initialTransferWithCallingNumber: correspondingDeployment.initialTransferWithCallingNumber ?? defaultTelephonyDeploymentSettings.initialTransferWithCallingNumber,
                        postProactiveResponsePauseDelayMS: correspondingDeployment.postProactiveResponsePauseDelayMS ?? defaultTelephonyDeploymentSettings.postProactiveResponsePauseDelayMS,
                        virtualNumber: correspondingDeployment.virtualNumber ?? defaultTelephonyDeploymentSettings.virtualNumber,
                        useVirtualNumber: correspondingDeployment.virtualNumber?.length > 0,
                        shouldProcessAudio: correspondingDeployment.shouldProcessAudio ?? defaultTelephonyDeploymentSettings.shouldProcessAudio,
                        disableSpamDetection: correspondingDeployment.disableSpamDetection ?? defaultTelephonyDeploymentSettings.disableSpamDetection,
                        immediateTransferHourCutoff: correspondingDeployment.immediateTransferHourCutoff ?? defaultTelephonyDeploymentSettings.immediateTransferHourCutoff,
                        aiReengageSecondsAfterImmediateTransfer: correspondingDeployment.aiReengageSecondsAfterImmediateTransfer ?? defaultTelephonyDeploymentSettings.aiReengageSecondsAfterImmediateTransfer,
                        spamDetectionPromptOverride: correspondingDeployment.spamDetectionPromptOverride ?? defaultTelephonyDeploymentSettings.spamDetectionPromptOverride,
                        spamDetectionTolerance: correspondingDeployment.spamDetectionTolerance ?? defaultTelephonyDeploymentSettings.spamDetectionTolerance,
                        spamDetectionTemperature: correspondingDeployment.spamDetectionTemperature ?? defaultTelephonyDeploymentSettings.spamDetectionTemperature,
                        spamDetectionGenAiProvider: correspondingDeployment.spamDetectionGenAiProvider ?? defaultTelephonyDeploymentSettings.spamDetectionGenAiProvider,
                        tryWebsocketReconnectionOnWelcome: correspondingDeployment.tryWebsocketReconnectionOnWelcome ?? defaultTelephonyDeploymentSettings.tryWebsocketReconnectionOnWelcome,
                        platform: correspondingDeployment.platform ?? PHONE_PLATFORMS.VONAGE,
                        useGenerativeProactiveResponse: correspondingDeployment.useGenerativeProactiveResponse ?? defaultTelephonyDeploymentSettings.useGenerativeProactiveResponse,
                        generativeProactiveResponsePrompt: correspondingDeployment.generativeProactiveResponsePrompt ?? defaultTelephonyDeploymentSettings.generativeProactiveResponsePrompt,
                        lastProactiveResponsesToDisallow: correspondingDeployment.lastProactiveResponsesToDisallow ?? defaultTelephonyDeploymentSettings.lastProactiveResponsesToDisallow,
                        notificationEmails: correspondingDeployment.notificationEmails ?? defaultTelephonyDeploymentSettings.notificationEmails,
                        outboundCallerIdNumber: correspondingDeployment.outboundCallerIdNumber ?? defaultTelephonyDeploymentSettings.outboundCallerIdNumber,
                        useForOutboundCalling: correspondingDeployment.useForOutboundCalling ?? defaultTelephonyDeploymentSettings.useForOutboundCalling,
                        disableAssistantSpeechCleaning: correspondingDeployment.disableAssistantSpeechCleaning ?? defaultTelephonyDeploymentSettings.disableAssistantSpeechCleaning,
                        optInText: correspondingDeployment.optInText ?? defaultTelephonyDeploymentSettings.optInText,
                    };

                    setLocaleConfigurations(prev => ({
                        ...prev,
                        [locale]: targetConfig
                    }));
                }
            }
        }

        // Update form values if we have a target config
        if (targetConfig) {
            Object.keys(targetConfig).forEach(key => {
                props.setFieldValue(key, (targetConfig as any)[key]);
            });
        }

        setSwitchingToLocale(null);
    };

    const handleOnChange = (optionValue: string, value: string | boolean | number) => {
        props.setFieldValue(optionValue, value);
        setModifiedLocales(prev => new Set(prev).add(activeLocale));
        // Also update the current locale configuration
        setLocaleConfigurations(prev => ({
            ...prev,
            [activeLocale]: { ...prev[activeLocale], [optionValue]: value }
        }));
    };
    const handleGenerativeProactiveResponseChange = (optionValue: string, value: string | boolean) => {
        handleOnChange(optionValue, value);
        if (value) {
            handleOnChange("postProactiveResponsePauseDelayMS", "100");
            handleOnChange("proactiveResponseDelayMS", "900");
        }
    };

    const handleTtsOptionChange = (index: number, field: 'ttsProvider' | 'ttsVoice' | 'both', value: string) => {
        if (field === "both") {
            const updated = [...props.values.ttsOptions]
            const values = value.split("split")
            updated[index] = {
                ...updated[index],
                ["ttsProvider"]: values[0]
            }
            updated[index] = {
                ...updated[index],
                ["ttsVoice"]: values[1]
            }
            props.setFieldValue('ttsOptions', updated);
            setModifiedLocales(prev => new Set(prev).add(activeLocale));
            // Update locale configuration
            setLocaleConfigurations(prev => ({
                ...prev,
                [activeLocale]: { ...prev[activeLocale], ttsOptions: updated as any }
            }));
        }
        else {
            const updated = [...props.values.ttsOptions];
            updated[index] = {
                ...updated[index],
                [field]: value,
            };
            props.setFieldValue('ttsOptions', updated);
            setModifiedLocales(prev => new Set(prev).add(activeLocale));
            // Update locale configuration
            setLocaleConfigurations(prev => ({
                ...prev,
                [activeLocale]: { ...prev[activeLocale], ttsOptions: updated as any }
            }));
        }
    };

    const handleVoiceDropdownChange = (optionValue: string, value: string) => {
        const option = voiceOptions.find(
            fullOption =>
                fullOption.value.toLowerCase() === value.toLowerCase()
        )

        const currentLength = [...props.values.ttsOptions].length
        const newTtsOptions = [...props.values.ttsOptions, {}];
        props.setFieldValue('ttsOptions', newTtsOptions);

        setModifiedLocales(prev => new Set(prev).add(activeLocale));
        // Update locale configuration first
        setLocaleConfigurations(prev => ({
            ...prev,
            [activeLocale]: { ...prev[activeLocale], ttsOptions: newTtsOptions as any }
        }));

        handleTtsOptionChange(currentLength, "both", option.provider + "split" + option.voice);
    };

    const handleDropDownChange = (optionValue: string, value: string | boolean) => {
        props.setFieldValue(optionValue, value);
        setModifiedLocales(prev => new Set(prev).add(activeLocale));
        // Update locale configuration
        setLocaleConfigurations(prev => ({
            ...prev,
            [activeLocale]: { ...prev[activeLocale], [optionValue]: value }
        }));
    };

    const handleCountryChange = (optionValue: string, value: string) => {
        props.setFieldValue("country", value);
        setModifiedLocales(prev => new Set(prev).add(activeLocale));
        // Update locale configuration
        setLocaleConfigurations(prev => ({
            ...prev,
            [activeLocale]: { ...prev[activeLocale], country: value }
        }));
    };

    // Wrapper for props.handleChange to update locale configurations
    const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value, type } = e.target;
        const fieldValue = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;

        props.handleChange(e);
        setModifiedLocales(prev => new Set(prev).add(activeLocale));
        // Update locale configuration
        setLocaleConfigurations(prev => ({
            ...prev,
            [activeLocale]: { ...prev[activeLocale], [name]: fieldValue }
        }));
    };
    const handleNotificationEmailsChange = (emails: string) => {
        props.setFieldValue("notificationEmails", emails);
        setModifiedLocales(prev => new Set(prev).add(activeLocale));
        // Update locale configuration
        setLocaleConfigurations(prev => ({
            ...prev,
            [activeLocale]: { ...prev[activeLocale], ["notificationEmails"]: emails }
        }));
    };

    // Handle submission of multiple locale configurations
    const handleMultiLocaleSubmit = async () => {
        const currentConfigurations = {
            ...localeConfigurations,
            [activeLocale]: { ...props.values }
        };

        const localesNeedingSubmission = new Set([...modifiedLocales, activeLocale]);

        const configurationsToSubmit = Object.entries(currentConfigurations)
            .filter(([locale]) => localesNeedingSubmission.has(locale));

        props.setSubmitting(true);

        try {
            let hasNewLocaleDeployments = false;

            // Submit each modified locale configuration separately
            for (const [locale, config] of configurationsToSubmit) {
                //if this is a new locale configuration for an existing number
                const isExistingNumber = !!props.selectedTelephonyDeployment?.phoneNumber;
                const isOriginalLocale = locale === props.selectedTelephonyDeployment?.locale;

                // Check if this locale already exists in the deployments for this number
                const isExistingLocale = props.selectedPhoneNumberDeployments &&
                    props.selectedPhoneNumberDeployments.some(deployment => deployment.locale === locale);

                const isNewLocaleForExistingNumber = isExistingNumber && !isOriginalLocale && !isExistingLocale;

                // Create TelephonyDeploymentRequest for this locale (similar to TelephonyDeploymentForm.tsx)
                const telephonyRequest: any = {
                    ...props.selectedTelephonyDeployment,
                    noTracking: config.noTracking,
                    phoneNumberCountryCode: isNewLocaleForExistingNumber ?
                        props.selectedTelephonyDeployment?.phoneNumberCountryCode : config.country,
                    phoneNumberStartsWith: config.phoneNumberStartsWith,
                    environmentId: props.environmentId,
                    ttsOptions: config.ttsOptions,
                    locale: config.locale,
                    asrEndpointing: config.asrEndpointing,
                    asrProvider: config.asrProvider,
                    asrModel: config.asrModel,
                    asrTier: config.asrTier,
                    performLanguageDetection: config.performLanguageDetection,
                    dtmfTurnsToListen: config.dtmfTurnsToListen,
                    ttsPronunciationsMap: config.ttsPronunciationsMap,
                    firstMessageDelayMS: config.firstMessageDelayMS,
                    mergeTurnsAfterPauses: config.mergeTurnsAfterPauses,
                    mergeTurnTimeoutMS: config.mergeTurnTimeoutMS,
                    disableBargeIn: config.disableBargeIn,
                    repromptAttempts: config.repromptAttempts,
                    repromptDelaySeconds: config.repromptDelaySeconds,
                    name: config.name || '', // Use the name as entered by the user
                    proactiveResponseDelayMS: config.proactiveResponseDelayMS,
                    proactiveResponses: config.proactiveResponses,
                    useProactiveResponses: config.useProactiveResponses,
                    maxProactiveResponseAttempts: config.maxProactiveResponseAttempts,
                    followUpProactiveResponses: config.followUpProactiveResponses,
                    followUpProactiveResponsesDelayMS: config.followUpProactiveResponsesDelayMS,
                    disableWelcomeMessageBargeIn: config.disableWelcomeMessageBargeIn,
                    disableSpeechStarted: config.disableSpeechStarted,
                    speechStartedUnconfirmedBargeInLimit: config.speechStartedUnconfirmedBargeInLimit,
                    speechStartedPauseMS: config.speechStartedPauseMS,
                    speechStartedRequiredBufferCount: config.speechStartedRequiredBufferCount,
                    callRecordingEnabled: config.callRecordingEnabled,
                    speechStartedLevel: config.speechStartedLevel,
                    attemptInitialTransfer: config.attemptInitialTransfer,
                    initialTransferTimeoutSeconds: config.initialTransferTimeoutSeconds,
                    initialTransferTarget: config.initialTransferTarget,
                    initialTransferWithCallingNumber: config.initialTransferWithCallingNumber,
                    postProactiveResponsePauseDelayMS: config.postProactiveResponsePauseDelayMS,
                    virtualNumber: config.virtualNumber,
                    useVirtualNumber: config.useVirtualNumber,
                    shouldProcessAudio: config.shouldProcessAudio,
                    disableSpamDetection: config.disableSpamDetection,
                    immediateTransferHourCutoff: config.autoTransferPreviouslyTransferred ?
                        (config.immediateTransferHourCutoff ?? 24) : undefined,
                    aiReengageSecondsAfterImmediateTransfer: config.aiReengageSecondsAfterImmediateTransfer,
                    spamDetectionPromptOverride: config.spamDetectionPromptOverride,
                    spamDetectionTolerance: config.spamDetectionTolerance,
                    spamDetectionTemperature: config.spamDetectionTemperature,
                    spamDetectionGenAiProvider: config.spamDetectionGenAiProvider,
                    tryWebsocketReconnectionOnWelcome: config.tryWebsocketReconnectionOnWelcome,
                    platform: config.platform,
                    useGenerativeProactiveResponse: config.useGenerativeProactiveResponse,
                    generativeProactiveResponsePrompt: config.generativeProactiveResponsePrompt,
                    lastProactiveResponsesToDisallow: config.lastProactiveResponsesToDisallow,
                    notificationEmails: config.notificationEmails,
                    outboundCallerIdNumber: config.outboundCallerIdNumber,
                    useForOutboundCalling: config.useForOutboundCalling,
                    disableAssistantSpeechCleaning: config.disableAssistantSpeechCleaning,
                    optInText: config.optInText,
                };

                // Process TTS options similar to original form
                if (telephonyRequest.ttsOptions) {
                    telephonyRequest.ttsOptions.forEach((option: any) => {
                        let voice = option.ttsVoice?.trim();
                        if (!voice?.length) {
                            option.ttsVoice = option.ttsProvider?.toLowerCase() === 'elevenlabs' ? 'defaultVoice' :
                                option.ttsProvider?.toLowerCase() === 'rime' ? 'defaultRimeVoice' : '';
                        }
                    });
                }

                if (isNewLocaleForExistingNumber) {
                    // For existing numbers with NEW locale configurations, use add methods
                    const platform = config.platform || props.selectedTelephonyDeployment?.platform || PHONE_PLATFORMS.VONAGE;
                    if (platform === PHONE_PLATFORMS.VONAGE) {
                        await telephonyDeploymentContainer.createTelephonyDeployment(props.appContainer?.state?.currentApp?.id, telephonyRequest);
                    } else if (platform === PHONE_PLATFORMS.TELNYX) {
                        await telephonyDeploymentContainer.createTelephonyDeployment(props.appContainer?.state?.currentApp?.id, telephonyRequest);
                    }
                    hasNewLocaleDeployments = true;
                } else {
                    //For new numbers or updating existing locale configurations, use existing logic
                    await props.submitTelephonyDeployment(telephonyRequest, props.selectedTelephonyDeployment?.phoneNumber);
                }
            }

            //If we created new locale deployments, get all the deployments to show all locales
            if (hasNewLocaleDeployments) {
                await telephonyDeploymentContainer.getTelephonyDeployments(props.appContainer?.state?.currentApp?.id);
            }
        } catch (error) {
            console.error('Error submitting locale configurations:', error);
        } finally {
            props.setSubmitting(false);
            props.closeTelephonyRequestForm();
        }
    };

    const getLocaleOptions = () => {
        const localeOptions = props.appContainer?.state?.currentApp?.languages.map((language) => {
            return { label: language.shortCode, value: language.shortCode }
        });
        return localeOptions;
    };

    const handlePhoneNumberStartsWithBlur = () => {
        if (props.values.phoneNumberStartsWith[0] !== "1" && props.values.platform === PHONE_PLATFORMS.VONAGE) {
            props.setFieldValue("phoneNumberStartsWith", 1 + props.values.phoneNumberStartsWith)
        }
        props.handleBlur;
    };

    const handleCreateNewLocaleFromDropdown = (selectedLocale: string) => {
        if (!localeConfigurations[selectedLocale]) {
            handleCreateNewLocaleConfiguration(selectedLocale);
        }
    };

    // Check if this feature should be available (existing number + multiple locales)
    const showCreateLocaleButton = !!props.selectedTelephonyDeployment?.phoneNumber && getLocaleOptions().length > 1;

    // Get available locales that don't have configurations yet
    const getAvailableLocalesForNewConfig = () => {
        const allLocales = getLocaleOptions();
        return allLocales.filter(locale => !localeConfigurations[locale.value]);
    };

    const getLocalesToShowAsTabs = () => {
        if (props.selectedPhoneNumberDeployments && props.selectedPhoneNumberDeployments.length > 1) {
            return props.selectedPhoneNumberDeployments.map(d => d.locale);
        } else {
            return Object.keys(localeConfigurations);
        }
    };

    const usingCustomVoice = props.values.ttsOptions[0].ttsProvider !== "Google" || props.values.ttsOptions[0].ttsVoice === "Custom" || !googleVoiceOptions.some(o => o.value === props.values.ttsOptions[0].ttsProvider);
    return (
        <ModalLayout isVisible={true}>
            {
                !props.selectedTelephonyDeployment?.phoneNumber ?
                    <ModalHeader title="Configure a Phone Number" onClose={() => props.closeTelephonyRequestForm()}
                        rightText={props.values.platform || PHONE_PLATFORMS.VONAGE}
                        rightTextClassName={platformTextStyle}
                    />
                    :
                    <HeaderContainer>
                        <ModalHeader
                            title={`Configure ${formatPhoneNumber(props.selectedTelephonyDeployment.phoneNumber, !!props.selectedTelephonyDeployment?.virtualNumber?.length)}`}
                            onClose={() => props.closeTelephonyRequestForm()}
                            rightText={props.selectedTelephonyDeployment.platform || props.values.platform || PHONE_PLATFORMS.VONAGE}
                            rightTextClassName={platformTextStyle}
                        />
                    </HeaderContainer>
            }

            {/* Locale Tabs - Show if there are multiple locale configurations OR multiple existing deployments */}
            {(Object.keys(localeConfigurations).length > 1 || (props.selectedPhoneNumberDeployments && props.selectedPhoneNumberDeployments.length > 1)) && (
                <LocaleTabsContainer>
                    {getLocalesToShowAsTabs().map(locale => (
                        <div key={locale} style={{ display: 'flex', alignItems: 'center' }}>
                            <LocaleTab
                                type="button"
                                isActive={activeLocale === locale}
                                isLoading={switchingToLocale === locale}
                                disabled={switchingToLocale !== null}
                                onClick={() => handleLocaleTabChange(locale)}
                            >
                                {switchingToLocale === locale ? 'Loading...' : locale}
                            </LocaleTab>
                            {getLocalesToShowAsTabs().length > 1 &&
                                !(props.selectedPhoneNumberDeployments && props.selectedPhoneNumberDeployments.some(d => d.locale === locale)) && (
                                    <RemoveLocaleButton
                                        type="button"
                                        onClick={() => {
                                            const newConfigurations = { ...localeConfigurations };
                                            delete newConfigurations[locale];
                                            setLocaleConfigurations(newConfigurations);

                                            // Switch to the first available locale if current one is deleted
                                            if (activeLocale === locale) {
                                                const remainingLocales = getLocalesToShowAsTabs().filter(l => l !== locale);
                                                if (remainingLocales.length > 0) {
                                                    const newActiveLocale = remainingLocales[0];
                                                    setActiveLocale(newActiveLocale);
                                                    const targetConfig = localeConfigurations[newActiveLocale] || newConfigurations[newActiveLocale];
                                                    if (targetConfig) {
                                                        Object.keys(targetConfig).forEach(key => {
                                                            props.setFieldValue(key, (targetConfig as any)[key]);
                                                        });
                                                    }
                                                }
                                            }
                                        }}
                                        title="Remove this locale configuration"
                                    >
                                        ×
                                    </RemoveLocaleButton>
                                )}
                        </div>
                    ))}
                </LocaleTabsContainer>
            )}

            <form onSubmit={(e) => {
                // Save current form values to current locale before submission
                setLocaleConfigurations(prev => ({
                    ...prev,
                    [activeLocale]: { ...props.values }
                }));

                // Use multi-locale submit if:
                // 1. There are multiple locale configurations, OR
                // 2. There are multiple existing deployments, OR  
                // 3. Multiple locales have been modified
                const shouldUseMultiLocaleSubmit =
                    Object.keys(localeConfigurations).length > 1 ||
                    (props.selectedPhoneNumberDeployments && props.selectedPhoneNumberDeployments.length > 1) ||
                    modifiedLocales.size > 1;

                if (shouldUseMultiLocaleSubmit) {
                    e.preventDefault();
                    handleMultiLocaleSubmit();
                } else {
                    props.handleSubmit(e);
                }
            }}>
                <CustomScrollbars autoHide autoHeight autoHeightMax={(windowSize.windowSize.innerHeight * .9) - MODAL_SCROLLBAR_SUBTRACTION}>
                    <ContentWrapper>
                        <SectionCard>
                            <SectionTitle>Basic Configuration</SectionTitle>
                            <NameTextField
                                name="name"
                                value={props.values.name}
                                label="Name"
                                placeholder="Enter a name for this telephony deployment here"
                                onChange={handleFormChange}
                                fieldContainerStyle={fieldContainerStyle}
                            />
                            <RadioGroupLabel>Track Analytics</RadioGroupLabel>
                            <CustomRadioGroup
                                checkContainerStyle={checkContainerStyle}
                                selectedOptionStyle={selectedOptionStyle}
                                onChange={(e) => handleOnChange("noTracking", e.value)}
                                value={props.values.noTracking}
                                options={noTrackingOptions}
                            />
                            <RecordCallsRadioGroupLabel>Record Calls</RecordCallsRadioGroupLabel>
                            <CustomRadioGroup
                                checkContainerStyle={checkContainerStyle}
                                selectedOptionStyle={selectedOptionStyle}
                                onChange={(e) => handleOnChange("callRecordingEnabled", e.value)}
                                value={props.values.callRecordingEnabled}
                                options={callRecordingOptions}
                            />
                            <RecordCallsRadioGroupLabel>Clean JSON and Markdown from Responses</RecordCallsRadioGroupLabel>
                            <CustomRadioGroup
                                checkContainerStyle={checkContainerStyle}
                                selectedOptionStyle={selectedOptionStyle}
                                onChange={(e) => handleOnChange("disableAssistantSpeechCleaning", e.value)}
                                value={props.values.disableAssistantSpeechCleaning}
                                options={SpeechCleaningOptions}
                            />
                        </SectionCard>

                        {(!props.selectedTelephonyDeployment?.phoneNumber) &&
                            <SectionCard>
                                <SectionTitle>Phone Number Configuration</SectionTitle>
                                <RadioGroupLabel>Phone Number Type</RadioGroupLabel>
                                <CustomRadioGroup
                                    checkContainerStyle={checkContainerStyle}
                                    selectedOptionStyle={selectedOptionStyle}
                                    onChange={(e) => handleOnChange("useVirtualNumber", e.value)}
                                    value={props.values.useVirtualNumber}
                                    options={virtualNumberOptions}
                                />
                                <RadioGroupLabel>Phone Platform</RadioGroupLabel>
                                <CustomRadioGroup
                                    checkContainerStyle={checkContainerStyle}
                                    selectedOptionStyle={selectedOptionStyle}
                                    onChange={(e) => handleOnChange("platform", e.value)}
                                    value={props.values.platform ?? PHONE_PLATFORMS.VONAGE}
                                    options={[{ label: "Vonage", value: PHONE_PLATFORMS.VONAGE }, { label: "Telnyx", value: PHONE_PLATFORMS.TELNYX }]}
                                />
                                {
                                    (!props.values.useVirtualNumber) &&
                                    <>
                                        <Label>Preferred Country Code and Area Code</Label>
                                        <PhoneNumberRow>
                                            <CountryDropdownWrapper>
                                                <CustomDropdown
                                                    handleOnChange={handleCountryChange}
                                                    fieldName="country"
                                                    options={countryOptions}
                                                    size="medium"
                                                    value={props.values.country || "US"}
                                                />
                                            </CountryDropdownWrapper>
                                            <PhoneNumberWrapper>
                                                <CustomTextField
                                                    name="phoneNumberStartsWith"
                                                    value={props.values.phoneNumberStartsWith}
                                                    placeholder="Enter your preferred country code and area code here e.g. 1508"
                                                    onChange={handleFormChange}
                                                    onBlur={handlePhoneNumberStartsWithBlur}
                                                    fieldContainerStyle={fieldContainerStyle}
                                                />
                                            </PhoneNumberWrapper>
                                        </PhoneNumberRow>
                                    </>
                                }
                                {
                                    (props.values.useVirtualNumber) &&
                                    <CustomTextField
                                        name="virtualNumber"
                                        value={props.values.virtualNumber}
                                        label={!props.selectedTelephonyDeployment?.phoneNumber?.length ? "Enter virtual number (Leave blank to generate)" : "Enter updated virtual number"}
                                        placeholder="Enter your preferred unique 6-12 digit virtual number here"
                                        onChange={handleFormChange}
                                        fieldContainerStyle={fieldContainerStyle}
                                    />
                                }
                            </SectionCard>
                        }

                        <SectionCard>
                            <SectionTitle>Voice & Language Settings</SectionTitle>
                            <LabelWrapper>
                                <Label>Add Preloaded Voices</Label>
                            </LabelWrapper>
                            <AddPreloadedVoicesDropdown
                                handleOnChange={handleVoiceDropdownChange}
                                fieldName="ttsVoice"
                                options={voiceOptions}
                                size="medium"
                                searchable={true}
                                placeholder="Select a voice to add"
                                className={dropDownStyleMedium}
                                value={usingCustomVoice ? "Custom" : props.values.ttsOptions[0][1]}
                            />
                            {
                                (usingCustomVoice) &&
                                <>
                                    {props.values.ttsOptions.map((option, index) => (
                                        <CustomVoiceRow key={index}>
                                            <CustomVoiceRowLeft
                                                name={`ttsOptions[${index}].ttsProvider`}
                                                value={option.ttsProvider}
                                                label="TTS Provider"
                                                labelStyle={labelStyle}
                                                placeholder="Enter your TTS provider here"
                                                onChange={(e) => handleTtsOptionChange(index, 'ttsProvider', e.target.value)}
                                                fieldContainerStyle={fieldContainerStyle}
                                            />
                                            <CustomVoiceRowRight
                                                name={`ttsOptions[${index}].ttsVoice`}
                                                value={option.ttsVoice}
                                                label="Voice"
                                                labelStyle={labelStyle}
                                                placeholder={
                                                    option.ttsProvider?.toLowerCase() === 'elevenlabs'
                                                        ? defaultVoice
                                                        : option.ttsProvider?.toLowerCase() === 'rime'
                                                            ? defaultRimeVoice
                                                            : ''
                                                }
                                                onChange={(e) => handleTtsOptionChange(index, 'ttsVoice', e.target.value)}
                                                fieldContainerStyle={fieldContainerStyle}
                                            />
                                            {props.values.ttsOptions.length > 1 && (
                                                <RemoveButton
                                                    type="button"
                                                    onClick={() => {
                                                        const updatedOptions = props.values.ttsOptions.filter((_, i) => i !== index);
                                                        props.setFieldValue('ttsOptions', updatedOptions);
                                                    }}
                                                    style={{ marginLeft: '8px' }}
                                                >
                                                    -
                                                </RemoveButton>
                                            )}
                                        </CustomVoiceRow>
                                    ))}
                                    <AddVoiceWrapper>
                                        <AddButton
                                            type="button"
                                            onClick={() => {
                                                const newOption = {};
                                                props.setFieldValue('ttsOptions', [...props.values.ttsOptions, newOption]);
                                            }}
                                        >
                                            + Add Another Voice
                                        </AddButton>
                                    </AddVoiceWrapper>
                                </>

                            }
                            <LabelWrapper>
                                <Label>Locale</Label>
                            </LabelWrapper>
                            {
                                getLocaleOptions().length > 1 ?
                                    <CustomDropdown
                                        handleOnChange={handleOnChange}
                                        fieldName="locale"
                                        options={getLocaleOptions()}
                                        size="medium"
                                        className={dropDownStyleMedium}
                                        value={props.values.locale}
                                    />
                                    :
                                    <CustomDropdown
                                        handleOnChange={handleOnChange}
                                        fieldName="locale"
                                        options={[{ label: "en-US", value: "en-US" }]}
                                        size="medium"
                                        className={dropDownStyleMedium}
                                        value={props.values.locale}
                                    />
                            }
                            {showCreateLocaleButton && getAvailableLocalesForNewConfig().length > 0 && (
                                <>
                                    <LabelWrapper>
                                        <Label>Locale Configuration</Label>
                                    </LabelWrapper>
                                    <CustomDropdown
                                        handleOnChange={(fieldName, value) => handleCreateNewLocaleFromDropdown(value as string)}
                                        fieldName="newLocale"
                                        options={getAvailableLocalesForNewConfig()}
                                        value=""
                                        placeholder="Select a locale to create configuration"
                                        size="medium"
                                        className={dropDownStyleMedium}
                                    />
                                </>
                            )}
                        </SectionCard>

                        <SectionCard>
                            <SectionTitle>Timing & Delay Settings</SectionTitle>
                            <CustomTextField
                                name="firstMessageDelayMS"
                                disabled={false}
                                tooltip="Number in milliseconds before the first assistant message is played"
                                tooltipStyle={tooltipStyle}
                                toolTipPosition="right"
                                value={props.values.firstMessageDelayMS}
                                label="First Message Delay (Milliseconds)"
                                placeholder="Enter a number in miliseconds here"
                                onChange={handleFormChange}
                                onBlur={props.handleBlur}
                                fieldContainerStyle={fieldContainerStyle}
                            />
                            <CustomTextField
                                name="asrEndpointing"
                                tooltip="Number in milliseconds between when the caller is done talking and Voicify starts processing input"
                                tooltipStyle={tooltipStyle}
                                toolTipPosition="right"
                                disabled={false}
                                value={props.values.asrEndpointing}
                                label="ASR Endpointing (Milliseconds)"
                                type="number"
                                placeholder="Enter a number in miliseconds here"
                                onChange={handleFormChange}
                                onBlur={props.handleBlur}
                                fieldContainerStyle={fieldContainerStyle}
                            />
                        </SectionCard>

                        <SectionCard>
                            <SectionTitle>Barge-In Settings</SectionTitle>
                            <RadioGroupLabel>Use Barge In</RadioGroupLabel>
                            <CustomRadioGroup
                                checkContainerStyle={checkContainerStyle}
                                selectedOptionStyle={selectedOptionStyle}
                                onChange={(e) => handleOnChange("disableBargeIn", e.value)}
                                value={props.values.disableBargeIn}
                                options={disableBargeInOptions}
                            />
                            {
                                !props.values.disableBargeIn &&
                                <>
                                    <AllowWelcomeMessageBargeInLabel>Allow Welcome Message Barge In</AllowWelcomeMessageBargeInLabel>
                                    <CustomRadioGroup
                                        checkContainerStyle={checkContainerStyle}
                                        selectedOptionStyle={selectedOptionStyle}
                                        onChange={(e) => handleOnChange("disableWelcomeMessageBargeIn", e.value)}
                                        value={props.values.disableWelcomeMessageBargeIn}
                                        options={disableWelcomeMessageBargeInOptions}
                                    />
                                    <MergeTurnsAfterPausesLabel>Merge Turns After Pauses</MergeTurnsAfterPausesLabel>
                                    <CustomRadioGroup
                                        checkContainerStyle={checkContainerStyle}
                                        selectedOptionStyle={selectedOptionStyle}
                                        onChange={(e) => handleOnChange("mergeTurnsAfterPauses", e.value)}
                                        value={props.values.mergeTurnsAfterPauses}
                                        options={mergeTurnsAfterPausesOptions}
                                    />
                                    <MergeTurnTimeoutTextField
                                        tooltip="The maximum duration of pause between caller utterances that qualifies them as a single utterance"
                                        tooltipStyle={tooltipStyle}
                                        toolTipPosition="right"
                                        name="mergeTurnTimeoutMS"
                                        type="number"
                                        disabled={false}
                                        value={props.values.mergeTurnTimeoutMS}
                                        label="Merge Turn Timeout (Milliseconds)"
                                        placeholder="Enter a number in miliseconds here"
                                        onChange={handleFormChange}
                                        onBlur={props.handleBlur}
                                        fieldContainerStyle={fieldContainerStyle}
                                    />
                                    <RadioGroupLabel>Use Speech Started Event</RadioGroupLabel>
                                    <CustomRadioGroup
                                        checkContainerStyle={checkContainerStyle}
                                        selectedOptionStyle={selectedOptionStyle}
                                        onChange={(e) => handleOnChange("disableSpeechStarted", e.value)}
                                        value={props.values.disableSpeechStarted}
                                        options={disableSpeechStartedOptions}
                                    />
                                    {
                                        !props.values.disableSpeechStarted &&
                                        <>
                                            <SpeechStartedLevelTextField
                                                tooltip='The confidence level at which the assistant will consider the user to be speaking. 0.0 is silence, 1.0 is full volume/confidence, 0.9 is the default.'
                                                tooltipStyle={tooltipStyle}
                                                toolTipPosition="right"
                                                name="speechStartedLevel"
                                                type="number"
                                                disabled={false}
                                                value={props.values.speechStartedLevel}
                                                label="Speech Started Level"
                                                placeholder="Enter a number from 0.0 to 1 here"
                                                onChange={handleFormChange}
                                                onBlur={props.handleBlur}
                                                fieldContainerStyle={fieldContainerStyle}
                                            />
                                            <CustomTextField
                                                tooltip="The time in milliseconds that the assistant will pause after a speech started event."
                                                tooltipStyle={tooltipStyle}
                                                toolTipPosition="right"
                                                name="speechStartedPauseMS"
                                                type="number"
                                                disabled={false}
                                                value={props.values.speechStartedPauseMS}
                                                label="Speech Started Pause Milliseconds"
                                                placeholder="Enter a number in MS here"
                                                onChange={handleFormChange}
                                                onBlur={props.handleBlur}
                                                fieldContainerStyle={fieldContainerStyle}
                                            />
                                            <CustomTextField
                                                tooltip="The maximum number of uncomfirmed speech started events during an assistant utterance before speech started is turned off"
                                                tooltipStyle={tooltipStyle}
                                                toolTipPosition="right"
                                                name="speechStartedUnconfirmedBargeInLimit"
                                                type="number"
                                                disabled={false}
                                                value={props.values.speechStartedUnconfirmedBargeInLimit}
                                                label="Unconfirmed Speech Started Event Limit"
                                                placeholder="Enter a number here"
                                                onChange={handleFormChange}
                                                onBlur={props.handleBlur}
                                                fieldContainerStyle={fieldContainerStyle}
                                            />
                                            <CustomTextField
                                                tooltip='The number of consecutive "speaking" audio buffers before speech started event is fired.'
                                                tooltipStyle={tooltipStyle}
                                                toolTipPosition="right"
                                                name="speechStartedRequiredBufferCount"
                                                type="number"
                                                disabled={false}
                                                value={props.values.speechStartedRequiredBufferCount}
                                                label="Speech Started Required Audio Buffer Count"
                                                placeholder="Enter a number here"
                                                onChange={handleFormChange}
                                                onBlur={props.handleBlur}
                                                fieldContainerStyle={fieldContainerStyle}
                                            />
                                        </>
                                    }
                                </>
                            }
                        </SectionCard>

                        <SectionCard>
                            <SectionTitle>Proactive Response Settings</SectionTitle>
                            <RadioGroupLabel>Use Proactive Responses</RadioGroupLabel>
                            <CustomRadioGroup
                                checkContainerStyle={checkContainerStyle}
                                selectedOptionStyle={selectedOptionStyle}
                                onChange={(e) => handleOnChange("useProactiveResponses", e.value)}
                                value={props.values.useProactiveResponses}
                                options={useProactiveOptions}
                            />
                            {
                                props.values.useProactiveResponses &&
                                <>
                                    <UseGenerativeProactiveResponsesLabel>Use Generative Proactive Responses</UseGenerativeProactiveResponsesLabel>
                                    <CustomRadioGroup
                                        checkContainerStyle={checkContainerStyle}
                                        selectedOptionStyle={selectedOptionStyle}
                                        onChange={(e) => handleGenerativeProactiveResponseChange("useGenerativeProactiveResponse", e.value)}
                                        value={props.values.useGenerativeProactiveResponse}
                                        options={useGenerativeProactiveOptions}
                                    />
                                    <ProactiveResponseDelayTextField
                                        name="proactiveResponseDelayMS"
                                        disabled={false}
                                        tooltip="How long an assistant is taking before it gives a proactive response"
                                        tooltipStyle={tooltipStyle}
                                        toolTipPosition="right"
                                        type="number"
                                        value={props.values.proactiveResponseDelayMS}
                                        label="Proactive Response Delay (Milliseconds)"
                                        placeholder="Enter a number in miliseconds here"
                                        onChange={handleFormChange}
                                        onBlur={props.handleBlur}
                                        fieldContainerStyle={fieldContainerStyle}
                                    />
                                    <CustomTextField
                                        name="postProactiveResponsePauseDelayMS"
                                        disabled={false}
                                        tooltip="How long an assistant pauses after giving a proactive response before giving the next response if ready."
                                        tooltipStyle={tooltipStyle}
                                        toolTipPosition="right"
                                        type="number"
                                        value={props.values.postProactiveResponsePauseDelayMS}
                                        label="Post Proactive Response Delay (Milliseconds)"
                                        placeholder="Enter a number in miliseconds here"
                                        onChange={handleFormChange}
                                        onBlur={props.handleBlur}
                                        fieldContainerStyle={fieldContainerStyle}
                                    />
                                    <CustomTextField
                                        name="proactiveResponses"
                                        tooltip="Each time a proactive response is returned it will be randomly selected from this list."
                                        tooltipStyle={tooltipStyle}
                                        toolTipPosition="right"
                                        disabled={false}
                                        value={props.values.proactiveResponses}
                                        label="Proactive responses"
                                        placeholder='Enter the proactive responses you want to use divided by a "|" here.'
                                        onChange={handleFormChange}
                                        onBlur={props.handleBlur}
                                        fieldContainerStyle={fieldContainerStyle}
                                    />
                                    <CustomTextField
                                        name="maxProactiveResponseAttempts"
                                        tooltip="Maximum number of times the assistant will give a proactive response for a single turn of the conversation."
                                        tooltipStyle={tooltipStyle}
                                        toolTipPosition="right"
                                        disabled={false}
                                        value={props.values.maxProactiveResponseAttempts}
                                        label="Maximum Proactive Response Attempts"
                                        placeholder='1'
                                        type="number"
                                        validate={(value) => {
                                            if (value < 1) {
                                                return "Value must be 1 or greater";
                                            }
                                        }}
                                        onChange={(e) => {
                                            const value = parseInt(e.target.value);
                                            if (!isNaN(value) && value >= 1) {
                                                handleFormChange(e);
                                            }
                                        }}
                                        onBlur={props.handleBlur}
                                        fieldContainerStyle={fieldContainerStyle}
                                    />
                                    {props.values.maxProactiveResponseAttempts > 1 && (
                                        <>
                                            <CustomTextField
                                                name="followUpProactiveResponses"
                                                tooltip="After the first proactive response, if the assistant is still waiting, it will use a follow-up response from this list."
                                                tooltipStyle={tooltipStyle}
                                                toolTipPosition="right"
                                                disabled={false}
                                                value={props.values.followUpProactiveResponses}
                                                label="Follow-up Proactive Responses"
                                                placeholder='Enter the follow-up proactive responses divided by a "|" here.'
                                                onChange={handleFormChange}
                                                onBlur={props.handleBlur}
                                                fieldContainerStyle={fieldContainerStyle}
                                            />
                                            <CustomTextField
                                                name="followUpProactiveResponsesDelayMS"
                                                tooltip="Delay between the follow up proactive responses"
                                                tooltipStyle={tooltipStyle}
                                                toolTipPosition="right"
                                                disabled={false}
                                                value={props.values.followUpProactiveResponsesDelayMS}
                                                label="Follow-up Proactive Responses Delay (Milliseconds)"
                                                placeholder='8000'
                                                onChange={handleFormChange}
                                                onBlur={props.handleBlur}
                                                fieldContainerStyle={fieldContainerStyle}
                                            />
                                        </>
                                    )}
                                    <ProactiveResponsePromptTextAreaField
                                        name="generativeProactiveResponsePrompt"
                                        tooltip="Prompting for the genai proactive responses"
                                        disabled={false}
                                        value={props.values.generativeProactiveResponsePrompt}
                                        label="Proactive Response Prompt"
                                        placeholder='Enter prompting to generate proactive responses (if left blank will use default from the backend)'
                                        onChange={handleFormChange}
                                        onBlur={props.handleBlur}
                                    />
                                    <CustomTextField
                                        name="lastProactiveResponsesToDisallow"
                                        tooltip="Maximum number turns before we allow a proactive response to be repeated."
                                        tooltipStyle={tooltipStyle}
                                        toolTipPosition="right"
                                        disabled={false}
                                        value={props.values.lastProactiveResponsesToDisallow}
                                        label="Last Proactive Responses to Disallow"
                                        placeholder='6'
                                        type="number"
                                        onChange={(e) => {
                                            const value = parseInt(e.target.value);
                                            if (!isNaN(value) && value >= 0) {
                                                handleFormChange(e);
                                            }
                                        }}
                                        onBlur={props.handleBlur}
                                        fieldContainerStyle={fieldContainerStyle}
                                    />
                                </>
                            }
                        </SectionCard>

                        <SectionCard>
                            <SectionTitle>Reprompt Settings</SectionTitle>
                            <CustomTextField
                                name="repromptAttempts"
                                tooltip="Max number of times the assistant will reprompt before hanging up"
                                tooltipStyle={tooltipStyle}
                                toolTipPosition="right"
                                disabled={false}
                                value={props.values.repromptAttempts}
                                label="Reprompt Attempts"
                                placeholder="Enter the max number of reprompts here"
                                onChange={handleFormChange}
                                onBlur={props.handleBlur}
                                fieldContainerStyle={fieldContainerStyle}
                            />
                            <CustomTextField
                                name="repromptDelaySeconds"
                                tooltip="Number of seconds of caller silence before the assistant reprompts"
                                tooltipStyle={tooltipStyle}
                                toolTipPosition="right"
                                disabled={false}
                                value={props.values.repromptDelaySeconds}
                                label="Reprompt Delay (Seconds)"
                                placeholder="Enter the number of seconds between reprompts here"
                                onChange={handleFormChange}
                                onBlur={props.handleBlur}
                                fieldContainerStyle={fieldContainerStyle}
                            />
                        </SectionCard>

                        <SectionCard>
                            <SectionTitle>Spam Filter Settings</SectionTitle>
                            <RadioGroupLabel>Use Spam Filter</RadioGroupLabel>
                            <CustomRadioGroup
                                checkContainerStyle={checkContainerStyle}
                                selectedOptionStyle={selectedOptionStyle}
                                onChange={(e) => handleOnChange("disableSpamDetection", e.value)}
                                value={props.values.disableSpamDetection}
                                options={disableSpamFilterOptions}
                            />
                            {!props.values.disableSpamDetection && (
                                <div>
                                    <CustomTextField
                                        name="spamDetectionPromptOverride"
                                        type="text"
                                        value={props.values.spamDetectionPromptOverride}
                                        label="Spam Detection Prompt (there is a default, use this to overrride)"
                                        onChange={handleFormChange}
                                    />

                                    <CustomTextField
                                        name="spamDetectionTolerance"
                                        tooltip="Minimum predicted probability to flag a call as spam"
                                        tooltipStyle={tooltipStyle}
                                        toolTipPosition="right"
                                        disabled={false}
                                        value={
                                            props.values.spamDetectionTolerance < 0
                                                ? 0.999
                                                : props.values.spamDetectionTolerance
                                        }
                                        label="Spam Detection Tolerance"
                                        placeholder="Enter a number between 0.981 to 1 (Anything below 0.981 runs a false positive risk. Leave blank for default 0.999)"
                                        onChange={handleFormChange}
                                        onBlur={props.handleBlur}
                                        fieldContainerStyle={fieldContainerStyle}
                                    />

                                    <CustomTextField
                                        name="spamDetectionTemperature"
                                        toolTipPosition="right"
                                        disabled={false}
                                        value={
                                            props.values.spamDetectionTemperature < 0
                                                ? 0.1
                                                : props.values.spamDetectionTemperature
                                        }
                                        label="Spam Detection Temperature"
                                        placeholder="Enter a number between 0 and 1 (Leave blank for default 0.1)"
                                        onChange={handleFormChange}
                                        onBlur={props.handleBlur}
                                        fieldContainerStyle={fieldContainerStyle}
                                    />

                                    <CustomTextField
                                        name="spamDetectionGenAiProvider"
                                        type="text"
                                        value={props.values.spamDetectionGenAiProvider}
                                        label="Spam Detection GenAI model (defaults to 4o-mini)"
                                        onChange={handleFormChange}
                                    />
                                </div>
                            )}
                        </SectionCard>

                        <SectionCard>
                            <SectionTitle>Transfer Settings</SectionTitle>
                            <CustomTextField
                                tooltip='Default phone number to transfer to. 12223334444 format (11 digits with leading 1)'
                                tooltipStyle={tooltipStyle}
                                toolTipPosition="right"
                                name="initialTransferTarget"
                                disabled={false}
                                value={props.values.initialTransferTarget}
                                label="Default Transfer Phone Number"
                                placeholder="Enter a phone number here in format 12223334444"
                                onChange={handleFormChange}
                                onBlur={props.handleBlur}
                                fieldContainerStyle={fieldContainerStyle}
                            />
                            <AttemptInitialTransferRadioGroupLabel>Attempt Initial Transfer with Assistant as Backup</AttemptInitialTransferRadioGroupLabel>
                            <CustomRadioGroup
                                checkContainerStyle={checkContainerStyle}
                                selectedOptionStyle={selectedOptionStyle}
                                onChange={(e) => handleOnChange("attemptInitialTransfer", e.value)}
                                value={props.values.attemptInitialTransfer}
                                options={attemptInitialTransferOptions}
                            />
                            {
                                props.values.attemptInitialTransfer &&
                                <>
                                    <CustomTextField
                                        tooltip="The time in seconds that the phone will ring at the store before the assistant answers"
                                        tooltipStyle={tooltipStyle}
                                        toolTipPosition="right"
                                        name="initialTransferTimeoutSeconds"
                                        type="number"
                                        disabled={false}
                                        value={props.values.initialTransferTimeoutSeconds}
                                        label="Initial transfer ring time"
                                        placeholder="Enter a number in seconds here"
                                        onChange={handleFormChange}
                                        onBlur={props.handleBlur}
                                        fieldContainerStyle={fieldContainerStyle}
                                    />
                                    <RadioGroupLabel>Perform Initial Transfer with Calling Number Instead of Called Number</RadioGroupLabel>
                                    <CustomRadioGroup
                                        checkContainerStyle={checkContainerStyle}
                                        selectedOptionStyle={selectedOptionStyle}
                                        onChange={(e) => handleOnChange("initialTransferWithCallingNumber", e.value)}
                                        value={props.values.initialTransferWithCallingNumber}
                                        options={initialTransferWithCallingNumberOptions}
                                    />
                                </>
                            }
                            <RadioGroupLabel>Auto-Transfer Previously Immediately Transferred Users</RadioGroupLabel>
                            <CustomRadioGroup
                                checkContainerStyle={checkContainerStyle}
                                selectedOptionStyle={selectedOptionStyle}
                                onChange={(e) => {
                                    handleOnChange("autoTransferPreviouslyTransferred", e.value);
                                    if (e.value && (props.values.immediateTransferHourCutoff == null || props.values.immediateTransferHourCutoff === undefined)) {
                                        handleOnChange("immediateTransferHourCutoff", defaultTelephonyDeploymentSettings.immediateTransferHourCutoff);
                                    }
                                }}
                                value={props.values.autoTransferPreviouslyTransferred}
                                options={autoTransferPreviouslyTransferredOptions}
                            />
                            {props.values.autoTransferPreviouslyTransferred &&
                                <CustomTextField
                                    tooltip="If a user has been previously immediately transferred and calls back, this is the number of hours between the previous transfer and the current call that will be used to determine if the user should be automtically transferred again."
                                    tooltipStyle={tooltipStyle}
                                    toolTipPosition="right"
                                    name="immediateTransferHourCutoff"
                                    type="number"
                                    disabled={false}
                                    value={props.values.immediateTransferHourCutoff ?? defaultTelephonyDeploymentSettings.immediateTransferHourCutoff}
                                    label="Auto transfer previously immediately transferred users cutoff (hours)"
                                    placeholder="Enter a number in hours here"
                                    onChange={handleFormChange}
                                    onBlur={props.handleBlur}
                                    fieldContainerStyle={fieldContainerStyle}
                                />
                            }

                            <CustomTextField
                                tooltip='Allow AI to talk to the customer again, if no one answered the phone after an immediate transfer'
                                tooltipStyle={tooltipStyle}
                                toolTipPosition="right"
                                name="aiReengageSecondsAfterImmediateTransfer"
                                disabled={false}
                                type="number"
                                value={props.values.aiReengageSecondsAfterImmediateTransfer}
                                label="Reengage AI n Seconds After Immediate Transfer, If Target Unresponsive"
                                placeholder="Enter the threshold in seconds"
                                onChange={handleFormChange}
                                onBlur={props.handleBlur}
                                fieldContainerStyle={fieldContainerStyle}
                            />

                            <NotificationEmailsManager
                                value={props.values.notificationEmails}
                                onChange={(value) => handleNotificationEmailsChange(value)}
                                onBlur={() => props.handleBlur("notificationEmails")}
                                fieldContainerStyle={fieldContainerStyle}
                            />
                        </SectionCard>

                        {props.values.platform === PHONE_PLATFORMS.TELNYX && (
                            <SectionCard>
                                <SectionTitle>Outbound Calling</SectionTitle>
                                <LabelWrapper>
                                    <RadioGroupLabel>Use For Outbound Calling</RadioGroupLabel>
                                    <IconTooltip text='If multiple phone numbers are configured for the application, the outbound calling assistant will use this phone number if set to "Yes"' place="right" className={tooltipStyle} />
                                </LabelWrapper>
                                <CustomRadioGroup
                                    checkContainerStyle={checkContainerStyle}
                                    selectedOptionStyle={selectedOptionStyle}
                                    onChange={(e) => handleOnChange("useForOutboundCalling", e.value)}
                                    value={props.values.useForOutboundCalling}
                                    options={useForOutboundCallingOptions}
                                />
                                <CustomTextField
                                    tooltip='The caller ID number that will be displayed to recipients when making outbound calls. Must be verified on Telnyx.'
                                    tooltipStyle={tooltipStyle}
                                    toolTipPosition="right"
                                    name="outboundCallerIdNumber"
                                    disabled={false}
                                    value={props.values.outboundCallerIdNumber}
                                    label="Caller ID Number"
                                    placeholder="Enter caller ID number (e.g., 12223334444)"
                                    onChange={handleFormChange}
                                    onBlur={props.handleBlur}
                                    fieldContainerStyle={fieldContainerStyle}
                                />
                            </SectionCard>
                        )}

                        <SectionCard>
                            <SectionTitle>SMS</SectionTitle>
                            <CustomTextField
                                name="optInText"
                                disabled={false}
                                value={props.values.optInText}
                                label="Opt In Text"
                                placeholder="Enter SMS opt-in text"
                                onChange={handleFormChange}
                                onBlur={props.handleBlur}
                                fieldContainerStyle={fieldContainerStyle}
                            />
                        </SectionCard>

                        <ContentCollapsingSection title="Advanced Settings" icon={advancedSettingsIcon} isCollapsedByDefault={true} className={collapsedSection}>
                            <LabelWrapper>
                                <Label>ASR Provider</Label>
                            </LabelWrapper>
                            <CustomDropdown
                                handleOnChange={handleDropDownChange}
                                fieldName="asrProvider"
                                options={availableASRProviders.map(asr => ({ value: asr, label: asr }))}
                                size="medium"
                                className={dropDownStyleMedium}
                                value={props.values.asrProvider}
                            />
                            <CustomTextField
                                name="asrModel"
                                disabled={false}
                                value={props.values.asrModel}
                                label="ASR Model"
                                placeholder="Type your preferred ASR model here"
                                onChange={handleFormChange}
                                onBlur={props.handleBlur}
                                fieldContainerStyle={fieldContainerStyle}
                            />
                            <CustomTextField
                                name="asrTier"
                                disabled={false}
                                value={props.values.asrTier}
                                label="ASR Tier"
                                placeholder="Type your preferred ASR tier here"
                                onChange={handleFormChange}
                                onBlur={props.handleBlur}
                                fieldContainerStyle={fieldContainerStyle}
                            />
                            <RadioGroupLabel>Process Audio</RadioGroupLabel>
                            <CustomRadioGroup
                                checkContainerStyle={checkContainerStyle}
                                selectedOptionStyle={selectedOptionStyle}
                                onChange={(e) => handleOnChange("shouldProcessAudio", e.value)}
                                value={props.values.shouldProcessAudio}
                                options={processAudioOptions}
                            />
                            <RadioGroupLabel>On Welcome Reprompt Transfer or Recconnect</RadioGroupLabel>
                            <CustomRadioGroup
                                checkContainerStyle={checkContainerStyle}
                                selectedOptionStyle={selectedOptionStyle}
                                onChange={(e) => handleOnChange("tryWebsocketReconnectionOnWelcome", e.value)}
                                value={props.values.tryWebsocketReconnectionOnWelcome}
                                options={tryWebsocketReconnectionOnWelcomeOptions}
                            />
                            <CustomTextField
                                name="ttsPronunciationsMap"
                                disabled={false}
                                value={props.values.ttsPronunciationsMap}
                                label="TTS Pronunciations Map"
                                placeholder="example: {'pan': '{p11@n}'}, format: escaped json"
                                onChange={handleFormChange}
                                onBlur={props.handleBlur}
                                fieldContainerStyle={fieldContainerStyle}
                            />
                            <CustomTextField
                                name="notificationEmails"
                                disabled={false}
                                value={props.values.notificationEmails}
                                label="Notification Emails"
                                tooltip="Comma separated email addresses that will receive notifications when someone calls this number - for monitoring."
                                tooltipStyle={tooltipStyle}
                                toolTipPosition="right"
                                placeholder="example: email1@voicify.com, email2@voicify.com"
                                onChange={handleFormChange}
                                onBlur={props.handleBlur}
                                fieldContainerStyle={fieldContainerStyle}
                            />
                            <RadioGroupLabel>Perform Language Detection</RadioGroupLabel>
                            <CustomRadioGroup
                                checkContainerStyle={checkContainerStyle}
                                selectedOptionStyle={selectedOptionStyle}
                                onChange={(e) => handleOnChange("performLanguageDetection", e.value)}
                                value={props.values.performLanguageDetection}
                                options={[
                                    { label: "Yes", value: true },
                                    { label: "No", value: false },
                                ]}
                            />
                            <CustomTextField
                                name="dtmfTurnsToListen"
                                type="number"
                                tooltip="Number of turns to listen for DTMF (dial tone) input. Set to 0 to disable."
                                tooltipStyle={tooltipStyle}
                                toolTipPosition="right"
                                disabled={false}
                                value={props.values.dtmfTurnsToListen}
                                label="DTMF Turns to Listen"
                                placeholder="Enter number of turns (0 to disable)"
                                onChange={handleFormChange}
                                onBlur={props.handleBlur}
                                fieldContainerStyle={fieldContainerStyle}
                            />
                        </ContentCollapsingSection>
                    </ContentWrapper>
                </CustomScrollbars>
                <FooterBar>
                    <Button
                        disabled={props.isSubmitting}
                        loading={props.isSubmitting}
                        type="submit"
                        themes={['primary', 'start']}
                        text="Save & Close"
                        onClick={(e) => {
                            // Debug form validation
                            console.log('Form errors:', props.errors);
                            console.log('Form values:', props.values);
                            console.log('Form isValid:', props.isValid);


                            // If there are errors, prevent default submission and show them
                            if (!props.isValid) {
                                e.preventDefault();
                                console.error('Form has validation errors:', props.errors);
                                return;
                            }
                        }}
                    />
                    <Button disabled={props.isSubmitting} loading={props.isSubmitting} type="button" themes={['secondary', 'end']} text="Cancel" onClick={() => props.closeTelephonyRequestForm()} />
                </FooterBar>
            </form >
        </ModalLayout >
    )
}

const customRadioGroupStyle = css`
    margin-bottom: 0;
`;

const AddPreloadedVoicesDropdown = styled(StyledDropdown)`
    margin: 0 0 32px 0;
`

const AddVoiceWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
`;

const RemoveButton = styled.button`
    background: none;
    border: none;
    color: ${color_text_link};
    font-size: 24px;
    line-height: 1;
    cursor: pointer;
    padding: 0;
    margin-left: 8px;

    &:hover {
        color: grey;
    }
`;

const AddButton = styled.button`
    background: none;
    border: none;
    color: ${color_text_link};
    font-size: 14px;
    line-height: 1;
    cursor: pointer;
    padding: 0;
    margin: 0 18px 24px 0;
    max-width: 1000px;

    &:hover {
        color: darkgrey;
    }
`;

const collapsedSection = css`
    *.card-title-container {
        padding: 24px 24px 24px 40px;
        h4 {
            font-size: 14px;
        }
    }
`;

const RadioGroupLabel = styled.p`
    font-weight: 400;
    font-size: 14px;
    color: ${color_shades_darkest};
    margin: 0;
`;

const CustomRadioGroup = styled(RadioGroup)`
    margin: 0 0 26px 0;
`;

const AttemptInitialTransferRadioGroupLabel = styled(RadioGroupLabel)`
    margin-top: 24px;
`;

const RecordCallsRadioGroupLabel = styled(RadioGroupLabel)`
    margin-top: 24px;
`;

const UseGenerativeProactiveResponsesLabel = styled(RadioGroupLabel)`
    margin-top: 24px;
`;

const AllowWelcomeMessageBargeInLabel = styled(RadioGroupLabel)`
    margin-top: 24px;
`;

const MergeTurnsAfterPausesLabel = styled(RadioGroupLabel)`
    margin-top: 24px;
`;

const CustomDropdown = styled(StyledDropdown)`
    margin: 0 0 24px 0;
`;

const CustomTextField = styled(TextField)`
    margin-bottom: 0 0 26px 0;
    label {
        font-size: 14px;
    }
`;

const ProactiveResponseDelayTextField = styled(CustomTextField)`
    margin-top: 24px;
`;

const MergeTurnTimeoutTextField = styled(CustomTextField)`
    margin-top: 24px;
`;

const SpeechStartedLevelTextField = styled(CustomTextField)`
    margin-top: 24px;
`;

const NameTextField = styled(CustomTextField)`
    margin-bottom: 24px;
`;

const ProactiveResponsePromptTextAreaField = styled(TextAreaField)`
    margin: 0 0 24px 0;
    label {
        font-size: 14px;
        margin: 0;
        font-weight: normal;
    }
`;

const LabelWrapper = styled.div`
    display: flex;
    flex-direction: row;
`;

const Label = styled.p`
    font-weight: 400;
    font-size: 14px;
    color: ${color_shades_darkest};
    margin: 0 0 8px 0px;
`;

const dropDownStyleMedium = css`
    margin-bottom: 32px;
`;

const tooltipStyle = css`
    margin-left: 8px;
`;

const labelStyle = css`
    font-weight: 400;
    font-size: 14px;
    color: ${color_shades_darkest};
    margin: 0 0 4px 0px;
`;

const checkContainerStyle = css`
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background-color: #ffffff;
    border: ${style_border_default};
    display: flex;
    align-items: center;
    justify-content: center;
`;

const selectedOptionStyle = css`
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background-image: ${color_gradients_green_shift};
`;

const fieldContainerStyle = css`
    height: 48px;
`;

const ContentWrapper = styled.div`
    display: flex;
    flex-direction: column;
    margin: 36px 36px 0 36px;
`;


const CustomVoiceRow = styled.div`
    display: flex;
    flex-direction: row;
    height: 85px;
    align-items: center;
    margin-top: 14px;
`;

const CustomVoiceRowLeft = styled(CustomTextField)`
    margin-right: 8px;
    //label style
    label {
        margin-bottom: 0px;
    }
`;

const CustomVoiceRowRight = styled(CustomTextField)`
    margin-left: 8px;
    label {
        margin-bottom: 0px;
    }
`;

const PhoneNumberRow = styled.div`
    display: flex;
    align-items: flex-end;  // Changed from center to flex-end
    gap: 8px;
`;

const CountryDropdownWrapper = styled.div`
    flex: 0 0 160px;
`;

const PhoneNumberWrapper = styled.div`
    flex: 1;
`;

const SectionCard = styled.div`
    margin-bottom: 24px;
    padding: 24px;
    border: 1px solid #E5E5E5;
    border-radius: 4px;
    background-color: #FFFFFF;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
`;

const SectionTitle = styled.h3`
    font-size: 16px;
    font-weight: 600;
    color: ${color_shades_darkest};
    margin: 0 0 24px 0;
`;

const platformTextStyle = css`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 20px;
    color: ${color_shades_darkest};
    font-weight: 500;
    margin-right: 16px;
    img {
        width: 24px;
        height: 24px;
    }
`;

const HeaderContainer = styled.div`
    position: relative;
`;

const LocaleTabsContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 16px 36px 0 36px;
    border-bottom: 1px solid ${color_shades_light};
    padding-bottom: 8px;
`;

const LocaleTab = styled.button<{ isActive: boolean; isLoading?: boolean }>`
    padding: 8px 16px;
    border: none;
    background: ${props => props.isActive ? color_gradients_green_shift : 'transparent'};
    color: ${props => props.isActive ? color_defaults_white : color_shades_darkest};
    border-radius: 4px;
    font-size: 14px;
    font-weight: ${props => props.isActive ? '600' : '400'};
    cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
    opacity: ${props => props.isLoading ? 0.7 : 1};
    transition: all 0.2s ease;

    &:hover {
        background: ${props => props.isActive ? color_gradients_green_shift : props.disabled ? 'transparent' : color_shades_lighter};
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

const RemoveLocaleButton = styled.button`
    background: none;
    border: none;
    color: ${color_text_link};
    font-size: 12px;
    margin-left: 8px;
    cursor: pointer;
    
    &:hover {
        color: ${color_colors_decline};
    }
`;

export default TelephonyDeployment;